/**
 * 租户管理 API
 * 对应 foundation-service (7092)
 *
 * 包含:
 * - 组织层级管理 (创建子组织、组织树)
 * - 用户-租户关联 (用户切换租户、获取可访问租户列表)
 * - 租户成员管理 (分配用户、移除用户、角色设置)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';

// ==================== 类型定义 ====================

/** 租户类型 */
export type TenantType = 'root' | 'branch' | 'franchise';

/** 租户状态 */
export type TenantStatus = 'trial' | 'active' | 'expired' | 'frozen' | 'cancelled';

export const TenantStatusMap = {
  trial: '试用中',
  active: '正式激活',
  expired: '已过期',
  frozen: '已冻结',
  cancelled: '已注销',
} as const;

/** 租户基础信息 */
export interface Tenant {
  id: number;
  parent_id: number;
  name: string;
  code: string;
  type: TenantType;
  status: TenantStatus;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  logo_url: string;
  address: string;
  industry: string;
  scale: string;
  remark: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  children?: Tenant[];
}

/** 创建子组织请求 */
export interface CreateSubTenantReq {
  parent_id: number;
  name: string;
  code: string;
  type?: TenantType;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  logo_url?: string;
  address?: string;
  industry?: string;
  scale?: string;
  remark?: string;
  copy_config?: boolean;
}

/** 更新租户请求 */
export interface UpdateTenantReq {
  name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  logo_url?: string;
  address?: string;
  industry?: string;
  scale?: string;
  remark?: string;
  status?: TenantStatus;
}

/** 简化角色信息（keysToCamel 转换后的 camelCase 字段） */
export interface SimpleRole {
  id: number;
  tenantId: number;
  name: string;
  code: string;
  description: string;
  status: number;
}

/** 用户租户详细信息（keysToCamel 转换后的 camelCase 字段） */
export interface UserTenantInfo {
  userTenantId: number;
  tenantId: number;
  tenantName: string;
  tenantCode: string;
  tenantLogo: string;
  isPrimary: number;
  status: number;
  joinedAt: string;
  roles: SimpleRole[];
}

/** 租户成员信息（keysToCamel 转换后的 camelCase 字段） */
export interface TenantMember {
  userTenantId: number;
  userId: number;
  username: string;
  realName: string;
  phone: string;
  email: string;
  avatar: string;
  isPrimary: number;
  status: number;
  joinedAt: string;
  inviterName?: string;
  roles: SimpleRole[];
}

/** 批量分配用户请求 */
export interface BatchAssignUsersReq {
  user_ids: number[];
  role_ids?: number[];
}

/** 设置用户租户角色请求 */
export interface SetUserTenantRolesReq {
  role_ids: number[];
}

/** 切换租户请求 */
export interface SwitchTenantReq {
  tenant_id: number;
}

/** 切换租户响应（keysToCamel 转换后的 camelCase 字段） */
export interface SwitchTenantResp {
  tenantId: number;
  tenantName: string;
  tenantCode: string;
  roles: SimpleRole[];
}

/** 租户树响应 */
export interface TenantTreeResp {
  tree: Tenant[];
}

/** 用户租户列表响应 */
export interface UserTenantsResp {
  tenants: UserTenantInfo[];
}

/** 租户成员列表响应 */
export interface TenantMembersResp {
  members: TenantMember[];
  total: number;
}

// ==================== 租户层级管理 API ====================

/** 获取租户树结构 */
export const getTenantTree = async (rootId?: number) => {
  return handleApiResponse(
    unifiedApi.service('tenant').get<TenantTreeResp>('/tenants/tree', { params: rootId ? { rootId } : {} })
  );
};

/** 获取单个租户详情 */
export const getTenantById = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('tenant').get<Tenant>(`/tenants/${id}`)
  );
};

/** 创建子组织 */
export const createSubTenant = async (data: CreateSubTenantReq) => {
  return handleApiResponse(
    unifiedApi.service('tenant').post<Tenant>('/tenants/sub-tenants', data)
  );
};

/** 更新租户信息 */
export const updateTenant = async (id: number, data: UpdateTenantReq) => {
  return handleApiResponse(
    unifiedApi.service('tenant').put<Tenant>(`/tenants/${id}`, data)
  );
};

/** 删除租户 */
export const deleteTenant = async (id: number) => {
  return handleApiResponse(
    unifiedApi.service('tenant').delete<null>(`/tenants/${id}`)
  );
};

/** 获取子组织列表 */
export const getSubTenants = async (_parentId?: number) => {
  return handleApiResponse(
    unifiedApi.service('tenant').get<{ list: Tenant[]; total: number }>('/tenants/sub-tenants')
  );
};

// ==================== 用户-租户关联 API ====================

/** 获取当前用户可访问的租户列表 */
export const getUserTenants = async () => {
  return handleApiResponse(
    unifiedApi.service('tenant').get<UserTenantsResp>('/user/tenants')
  );
};

/** 切换到指定租户 */
export const switchTenant = async (data: SwitchTenantReq) => {
  return handleApiResponse(
    unifiedApi.service('tenant').post<SwitchTenantResp>('/user/switch-tenant', data)
  );
};

// ==================== 租户成员管理 API ====================

/**
 * 获取当前租户成员列表
 * 注意：后端 API 不需要 tenantId 参数，从 JWT 中获取
 */
export const getTenantMembers = async (_tenantId: number, page = 1, pageSize = 20) => {
  return handleApiResponse(
    unifiedApi.service('tenant').get<{ list: TenantMember[]; total: number; page: number; page_size: number }>(
      '/tenants/members',
      { params: { page, page_size: pageSize } }
    )
  );
};

/**
 * 获取子组织成员列表
 */
export const getSubTenantMembers = async (subTenantId: number, page = 1, pageSize = 20) => {
  return handleApiResponse(
    unifiedApi.service('tenant').get<{ list: TenantMember[]; total: number; page: number; page_size: number }>(
      `/tenants/sub-tenants/${subTenantId}/members`,
      { params: { page, page_size: pageSize } }
    )
  );
};

/** 批量分配用户到当前租户 */
export const batchAssignUsersToTenant = async (_tenantId: number, data: BatchAssignUsersReq) => {
  return handleApiResponse(
    unifiedApi.service('tenant').post<null>('/tenants/members/batch', data)
  );
};

/** 从当前租户移除用户 */
export const removeUserFromTenant = async (_tenantId: number, userId: number) => {
  return handleApiResponse(
    unifiedApi.service('tenant').delete<null>('/tenants/members', { data: { user_id: userId } })
  );
};

/** 设置用户在租户中的角色 */
export const setUserTenantRoles = async (
  _tenantId: number,
  userId: number,
  data: SetUserTenantRolesReq
) => {
  return handleApiResponse(
    unifiedApi.service('tenant').put<null>(
      `/tenants/members/${userId}/roles`,
      data
    )
  );
};

// ==================== API 对象导出 ====================

export const tenantManagementApi = {
  // 租户层级
  getTenantTree,
  getTenantById,
  createSubTenant,
  updateTenant,
  deleteTenant,
  getSubTenants,
  // 用户-租户关联
  getUserTenants,
  switchTenant,
  // 成员管理
  getTenantMembers,
  getSubTenantMembers,
  batchAssignUsersToTenant,
  removeUserFromTenant,
  setUserTenantRoles,
};

export default tenantManagementApi;
